import CustomTooltip from "@/components/layout-components/CustomToolTip"
import { COLORS } from "@/theme/localTheme"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  TextField as MUITextField,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
  Typography,
} from "@mui/material"
import { Fragment, useContext, useEffect, useMemo, useRef } from "react"
import { Controller, useFormContext, get } from "react-hook-form"
import { CustomSX, IFieldContext, IFieldOptions } from "../../../../../interface/FormGenerator"
import FieldContext from "../../FieldContext"
import DeleteChipIcon from "../../../../utils/assets/images/DeleteChipIcon.svg"
import Image from "next/image"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { validateRegex } from "../CJS"
import { ErrorSX } from "../../Styles/CommonStyles.sx"

const CheckOutlineIcon = () => <CheckBoxOutlineBlankIcon fontSize="medium" sx={{ color: "#fff" }} />
const CheckedIcon = () => <CheckBoxIcon fontSize="medium" sx={{ color: "#0094FF" }} />
const DeleteIcon = (props: any) => (
  <IconButton {...props} sx={{ padding: "5px" }}>
    <Image src={DeleteChipIcon} alt="D" width={10} height={10} />
  </IconButton>
)

const ChipMoreSX: SxProps = {
  "&": {
    width: "100%",
    height: "100%",
    display: "Flex",
    justifyContent: "center",
    alignItems: "center",
  },
}

const Dropdown = (props?: Partial<IFieldContext & Omit<SelectProps, "onChange">>) => {
  const { control, register, setValue, getValues, formState, clearErrors } = useFormContext()
  const { errors } = formState
  const contextData = useContext(FieldContext)
  const { formKey, form, onChange: onValueChange, ...rest } = Object.keys(contextData).length > 0 ? contextData : (props as IFieldContext)
  const selectProps: SelectProps = { ...rest }
  const tagLimit = form.comma_tags ? 4 : 4
  const hideLimit = form.comma_tags ? 5 : 5
  const hashMap = useRef<Record<string, string>>({})

  const error = get(errors, formKey)

  if (!form) throw new Error("Missing Parameter: Field Context")

  if (!formKey) throw new Error("Missing Parameter: Form Key")

  // This crap is useful if you want to send random ass shit to option props and still access it via onChange
  const memoOptionHash = useMemo(() => {
    if (!form?.options) return {}

    return form.options.reduce((acc: any, val) => {
      acc[val.value] = val
      return acc
    }, {})
  }, [form?.options])

  const DynamicSelectSX: SxProps = {
    "& fieldset legend span": {
      display: selectProps.displayEmpty ? "none" : "inline",
    },
    "& fieldset": {
      borderColor: "#3CA63C", // Important because of Hover
      top: form.multi_select && selectProps.displayEmpty ? 0 : undefined,
    },
    "& .MuiOutlinedInput-root:hover:not(.Mui-error) fieldset": {
      borderColor: "#3CA63C",
    },
  }

  const DynamicLabelSX: SxProps = selectProps.displayEmpty
    ? {
        "&": {
          color: "rgba(255, 255, 255, 0.70)",
          fontSize: "14px",
          letterSpacing: "0.15px",
          top: 0,
          left: 0,
          transform: "translate(14px, 16px) scale(1)",
          transformOrigin: "top left",
        },
      }
    : {}

  const SelectSX: CustomSX = {
    enable: {
      width: "100%",
      color: `${COLORS?.adminAppTextPrimary}`,
      "&": {
        borderRadius: "6px",
      },
      ".MuiFormLabel-root.Mui-focused": {
        color: COLORS?.adminAppTextPrimary,
      },
      ".MuiSvgIcon-root": {
        color: "white",
      },
      ".MuiOutlinedInput-root": {
        color: "white",
      },
      "&.MuiOutlinedInput-root:hover:not(.Mui-error) fieldset": {
        borderColor: "#3CA63C",
      },
    },
    disable: {
      "& .MuiOutlinedInput-input": {
        color: "rgba(255, 255, 255, 0.7)",
      },
      "& fieldset.MuiOutlinedInput-notchedOutline": {
        borderColor: `${COLORS?.adminAppTextPrimary} !important`,
      },
      "&:hover fieldset.MuiOutlinedInput-notchedOutline": {
        borderColor: `${COLORS?.adminAppTextPrimary}`,
      },
      "& .MuiInputBase-root.Mui-focused fieldset": {
        "&:hover fieldset": {
          borderColor: `${COLORS?.adminAppTextPrimary}`,
        },
        borderColor: `${COLORS?.adminAppTextPrimary}`,
      },
      "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline ": {
        borderColor: `${COLORS?.adminAppTextPrimary}`,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "rgba(255, 255, 255, 0.7)",
      },
    },
    common: {
      '&.MuiInputBase-root:not(:has(input[value=""])), &.NoOptionsAvail': {
        background: selectProps.displayEmpty ? "#3d423a" : undefined,
        zIndex: selectProps.displayEmpty ? 1 : undefined,
      },
    },
  }

  const AutoCompleteSX: SxProps = {
    "& .MuiInputBase-root": {
      flexWrap: "nowrap",
      ':has(.MuiChip-root), :not(:has(input[value=""]))': {
        background: "#3d423a",
        zIndex: 1,
      },
      [`& .MuiChip-root:nth-of-type(n+${hideLimit})`]: {
        display: "none",
      },
      ".MuiChip-root": {
        borderRadius: "100px",
        maxWidth: form.comma_tags ? "120px" : "150px",
        border: "1px solid rgba(255, 255, 255, 0.50)",
        background: "rgba(58, 207, 1, 0.20)",
        fontSize: "12px",
        letterSpacing: "0.16px",
        ".MuiSvgIcon-root": {
          color: "white",
        },
      },
      "span[class*=MuiAutocomplete-tag]": {
        borderRadius: "100px",
        border: "1px solid rgba(255, 255, 255, 0.50)",
        background: "rgba(75, 232, 15, 0.56)",
        fontSize: "12px",
        color: "white",
        width: "28px",
        height: "28px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  }

  const MenuSX: SxProps = {
    "&.MuiPaper-root, & .MuiPaper-root": {
      marginTop: "5px",
      boxShadow: "6px 9px 12px 0px rgba(0, 0, 0, 0.30), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)",
    },
    "& .MuiAutocomplete-noOptions": {
      background: "#363935",
    },
    "& .MuiList-root, & .MuiAutocomplete-listbox": {
      // ul
      background: "#363935",
      ".MuiMenuItem-root, .MuiAutocomplete-option": {
        // li
        height: "46px",
        fontSize: "16px",
        letterSpacing: "0.5px",
        ":hover": {
          backgroundColor: "rgba(255, 255, 255, 0.06)",
        },
      },
      ".MuiAutocomplete-option": {
        padding: "8px 16px 8px 16px",
        gap: "8px",
        '&[aria-selected="true"], &.Mui-focused, &[aria-selected="true"].Mui-focused': {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.06)",
          },
        },
        ".MuiCheckbox-root": {
          padding: 0,
          mr: "16px",
        },
      },
    },
  }

  const handleInputChange = (event: any, newValue: string) => {
    if (newValue && !newValue.startsWith(",") && newValue.endsWith(",") && newValue.length > 3) {
      const removeComma: string = newValue.substring(0, newValue.length - 1).trim()
      const newValues = { label: removeComma, value: removeComma }
      event.target.value = ""
      // console.info("HashMap ", hashMap.current, getValues(formKey))
      if (Object.keys(hashMap.current).length === 0 && getValues(formKey).length > 0) {
        // Prefill hashMap
        getValues(formKey).forEach((elem: IFieldOptions) => (hashMap.current[elem.value] = elem.value.toString()))
        // console.info("Hashing :", hashMap)
      }
      if (!hashMap.current.hasOwnProperty(removeComma)) {
        // Avoid Duplicate Entry
        setValue(formKey, [...(getValues(formKey) || []), newValues])
        hashMap.current[removeComma] = removeComma
      } else {
        event.target.blur()
        event.target.focus()
      }
    }
  }

  const onChipDeleted = (value: string) => {
    delete hashMap.current[value]
  }

  const onInputBlur = () => {
    var value = getValues(formKey)
    // console.info("onBlur :", value)
    if (value && value.length > 0) {
      clearErrors(formKey)
    }
  }

  return (
    <>
      <FormControl fullWidth>
        <InputLabel sx={DynamicLabelSX} id={`${formKey}_Select_Label`} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
          {form.label}
        </InputLabel>

        <Controller
          key={form?.id}
          defaultValue={form?.answer_value}
          name={formKey}
          control={control}
          rules={{
            required: typeof form.required === "boolean" && form.required ? "Required" : form.required,
            validate: (value) => {
              if (!form.required && !value) return undefined
              if (form.regex) {
                return validateRegex(value, form.regex)
              } else return undefined
            },
          }}
          render={({ field: { ref, value, onChange, ...fields } }) => {
            return (
              <>
                {form.multi_select ? (
                  <Autocomplete
                    sx={
                      {
                        ...(form.can_edit ? SelectSX.enable : SelectSX.disable),
                        ...SelectSX.common,
                        ...DynamicSelectSX,
                        ...AutoCompleteSX,
                      } as SxProps
                    }
                    freeSolo={false}
                    limitTags={tagLimit}
                    multiple
                    id={`${formKey}_AutoComplete`}
                    disableCloseOnSelect
                    options={form.comma_tags ? getValues(formKey) || [] : form.options || []}
                    getOptionLabel={(option) => (option?.label ? option.label : typeof option === "string" ? option : "No option")}
                    value={typeof value === "string" ? [] : getValues(formKey) || []}
                    onChange={(event, newValue) => {
                      onChange(newValue)
                      props?.onChange?.(newValue)
                    }}
                    forcePopupIcon={value?.length > 0 || (form?.options || [])?.length > 0}
                    onInputChange={form.comma_tags ? handleInputChange : () => {}}
                    renderInput={(params) => <MUITextField {...params} inputRef={ref} error={error?.message} />}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option, { selected }) => {
                      // @ts-ignore as key Exists.
                      const { key, ...others } = props
                      return (
                        <li key={option.value} {...others}>
                          <Checkbox icon={<CheckOutlineIcon />} checkedIcon={<CheckedIcon />} style={{ marginRight: 8 }} checked={selected} />
                          <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                            <>{option?.label ? option.label : typeof option === "string" ? option : "No option"}</>
                            <Typography color={"darkGray"}>{option.sub_value ? option.sub_value : null}</Typography>
                          </Box>
                        </li>
                      )
                    }}
                    getLimitTagsText={(more: number) => (
                      <CustomTooltip
                        title={
                          <Box display={"flex"} flexDirection={"column"}>
                            {(getValues(formKey) || [])
                              ?.slice(hideLimit)
                              ?.map((val: IFieldOptions, iodex: number) => (
                                <Box
                                  key={`ChipTT_${formKey}_${iodex}`}
                                >{`${iodex < 9 ? "0" : ""}${iodex + 4}: ${val?.alias || val?.label || ""}`}</Box>
                              ))}
                          </Box>
                        }
                      >
                        <Box sx={ChipMoreSX} onClick={(e) => e.stopPropagation()}>{`+${more}`}</Box>
                      </CustomTooltip>
                    )}
                    renderTags={(tagValue, getTagProps) => {
                      // console.info("Tag :", tagValue)
                      return tagValue.map((option, index) => (
                        <Fragment key={`${option.value}_${index}`}>
                          <CustomTooltip title={option?.label ? option.label : typeof option === "string" ? option : "No option"}>
                            <Chip
                              {...getTagProps({ index })}
                              deleteIcon={<DeleteIcon />}
                              onDelete={(e) => {
                                onChipDeleted(option.value)
                                getTagProps({ index }).onDelete(e)
                              }}
                              key={`${option.value}_${index}`}
                              label={option?.alias || option?.value || ""}
                            />
                          </CustomTooltip>
                        </Fragment>
                      ))
                    }}
                    noOptionsText={form.comma_tags ? "Add Minimum 3 Character Comma Seperated Values" : "No Options"}
                    readOnly={!form.can_edit}
                    popupIcon={<KeyboardArrowDownIcon />}
                    slotProps={{
                      paper: {
                        sx: MenuSX,
                      },
                    }}
                    onBlur={onInputBlur}
                  />
                ) : (
                  <Select
                    sx={{ ...(form.can_edit ? SelectSX.enable : SelectSX.disable), ...SelectSX.common, ...DynamicSelectSX } as SxProps}
                    className={value === "" && form?.options?.length === 0 ? "NoOptionsAvail" : ""}
                    labelId={`${formKey}_Select_Label`}
                    id={`${formKey}_Select`}
                    label={form?.label}
                    fullWidth
                    value={value}
                    onChange={(e, v: any) => {
                      const prevValue = getValues(formKey)
                      onChange(e)
                      props?.onChange?.(e.target.value)
                      if (typeof onValueChange === "function") {
                        // console.info("DEBUG :", e)
                        const currentValue = e?.target?.value ? { value: e.target.value } : v.props
                        currentValue.hash = { ...memoOptionHash }
                        onValueChange(currentValue, prevValue)
                      }
                    }}
                    inputRef={ref}
                    MenuProps={{
                      sx: MenuSX,
                    }}
                    readOnly={!form.can_edit}
                    IconComponent={KeyboardArrowDownIcon}
                    error={error?.message}
                    {...fields}
                    {...rest}
                  >
                    {form?.options?.map((option: any, idx: number) => (
                      <MenuItem value={option.value ?? option} key={option.value + "_" + idx}>
                        {option.label ?? option}
                      </MenuItem>
                    ))}
                    {!form?.options?.length ? (
                      <MenuItem value={""} sx={{ color: COLORS?.adminAppTextSecondary }}>
                        {"No Options"}
                      </MenuItem>
                    ) : null}
                  </Select>
                )}
              </>
            )
          }}
        />

        {(Boolean(form.required) || form.regex) && error?.message && (
          <Box className="ErrorVai" sx={ErrorSX}>
            <Typography fontSize={"0.75rem"}>{error?.message}</Typography>
          </Box>
        )}
      </FormControl>
    </>
  )
}

export default Dropdown
